import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { getContent, API } from "../lib/api";
import { BookOpenCheck, Check, RotateCcw, Volume2 } from "lucide-react";

const LEVELS = [
  { id: 0, label: "Nouveau",       interval_days: 0 },
  { id: 1, label: "Apprentissage", interval_days: 1 },
  { id: 2, label: "Familier",      interval_days: 3 },
  { id: 3, label: "Solide",        interval_days: 7 },
  { id: 4, label: "Mémorisé",      interval_days: 21 }
];
const STORAGE_KEY = "nour_hifz";

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}

function isDue(entry) {
  if (!entry || entry.level === 0) return true;
  const last = new Date(entry.last_at || 0).getTime();
  const next = last + (LEVELS[entry.level]?.interval_days || 1) * 86400000;
  return Date.now() >= next;
}

export default function Hifz() {
  const { t } = useApp();
  const [surahs, setSurahs] = useState([]);
  const [progress, setProgress] = useState(loadProgress);
  const [active, setActive] = useState(null);
  const [verses, setVerses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTrans, setShowTrans] = useState(true);

  useEffect(() => { getContent("juz_amma").then(setSurahs); }, []);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(progress)); }, [progress]);

  const due = useMemo(() => surahs.filter(s => isDue(progress[s.num])), [surahs, progress]);
  const memorized = useMemo(() => surahs.filter(s => (progress[s.num]?.level || 0) === 4).length, [surahs, progress]);

  const open = async (s) => {
    setActive(s);
    setVerses(null);
    setLoading(true);
    try {
      const [arabic, french] = await Promise.all([
        axios.get(`${API}/quran/surah/${s.num}`),
        axios.get(`${API}/quran/surah/${s.num}`, { params: { edition: "fr.hamidullah" } }).catch(() => null)
      ]);
      const ar = arabic.data?.data?.ayahs || [];
      const fr = french?.data?.data?.ayahs || [];
      setVerses(ar.map((v, i) => ({ ...v, translationFr: fr[i]?.text })));
    } catch {
      setVerses([]);
    } finally {
      setLoading(false);
    }
  };

  const review = (correct) => {
    if (!active) return;
    const cur = progress[active.num] || { level: 0, last_at: 0, reviews: 0 };
    const level = correct ? Math.min(4, cur.level + 1) : 0;
    setProgress(p => ({ ...p, [active.num]: { level, last_at: Date.now(), reviews: (cur.reviews || 0) + 1 } }));
    setActive(null);
  };

  const audio = (num) => {
    const url = `https://cdn.islamic.network/quran/audio-surah/128/ar.alafasy/${num}.mp3`;
    new Audio(url).play().catch(() => {});
  };

  const resetAll = () => { if (confirm("Réinitialiser toute la progression ?")) setProgress({}); };

  return (
    <div className="space-y-6 fade-in" data-testid="hifz-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><BookOpenCheck className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.hifz}</h1>
          <p className="text-sm text-nour-text2">Juz &apos;Amma · {memorized}/{surahs.length} mémorisées · {due.length} à réviser</p>
        </div>
      </header>

      <section className="nour-card p-5 bg-gradient-to-br from-emerald-50 to-emerald-100" data-testid="hifz-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-nour-text2">À réviser aujourd&apos;hui</div>
            <div className="font-heading text-3xl font-extrabold text-nour-primary">{due.length}</div>
          </div>
          <button className="btn-ghost" onClick={resetAll} data-testid="hifz-reset"><RotateCcw className="w-4 h-4"/> {t.reset}</button>
        </div>
        <div className="mt-4 h-2 bg-white rounded-full overflow-hidden">
          <div className="h-full bg-nour-primary transition-all" style={{ width: `${(memorized / Math.max(1, surahs.length)) * 100}%` }} />
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="hifz-list">
        {surahs.map(s => {
          const p = progress[s.num];
          const lvl = p?.level || 0;
          const dueNow = isDue(p);
          return (
            <button
              key={s.num}
              onClick={() => open(s)}
              data-testid={`hifz-surah-${s.num}`}
              className={`nour-card p-4 text-left hover:bg-emerald-50 transition flex items-center gap-3 ${dueNow ? "ring-1 ring-emerald-300" : ""}`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center shrink-0">
                <span className="font-heading font-bold text-nour-primary">{s.num}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-arabic-quran text-xl text-nour-primary truncate" dir="rtl">{s.name_ar}</div>
                <div className="text-sm font-heading font-semibold">{s.name_fr}</div>
                <div className="text-xs text-nour-text2">{s.ayahs} versets · Niveau {LEVELS[lvl].label}</div>
              </div>
              <div className="flex flex-col gap-0.5">
                {[0, 1, 2, 3, 4].map(i => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i < lvl ? "bg-nour-primary" : "bg-emerald-100"}`}></span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-2 sm:p-4" onClick={() => setActive(null)} data-testid="hifz-modal">
          <div className="nour-card p-5 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="font-arabic-quran text-3xl text-nour-primary" dir="rtl">{active.name_ar}</div>
                <h2 className="font-heading text-xl font-bold">{active.num}. {active.name_fr}</h2>
                <p className="text-xs text-nour-text2">{active.meaning_fr} · {active.ayahs} versets</p>
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost" onClick={() => audio(active.num)} data-testid="hifz-audio"><Volume2 className="w-4 h-4"/></button>
                <button className="btn-ghost" onClick={() => setShowTrans(s => !s)} data-testid="hifz-toggle-trans">{showTrans ? "AR" : "FR"}</button>
              </div>
            </div>

            <div className="space-y-3">
              {loading && <div className="text-sm text-nour-text2">Chargement…</div>}
              {verses && verses.map(v => (
                <div key={v.number} className="nour-card p-4">
                  <div className="font-arabic-quran text-2xl text-right text-nour-primary" dir="rtl">
                    {v.text} <span className="inline-block ml-1 text-base text-nour-text2">۝{v.numberInSurah}</span>
                  </div>
                  {showTrans && v.translationFr && <div className="text-sm mt-2 text-nour-text2">{v.translationFr}</div>}
                </div>
              ))}
              {verses && verses.length === 0 && <div className="text-sm text-red-700">Erreur de chargement.</div>}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button className="btn-ghost" onClick={() => review(false)} data-testid="hifz-review-fail">À retravailler</button>
              <button className="btn-primary" onClick={() => review(true)} data-testid="hifz-review-pass"><Check className="w-4 h-4"/> Récité avec succès</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
